// =============================================================================
// MyHorizonScreen.tsx — user-facing name "MY SCHEDULE" (/my-schedule).
// "Horizon" survives as the internal/technical name and as the timeline view's
// label, per James's requirements doc (3 Sep): "Rename My Horizon to My
// Schedule … Horizon can remain an internal/technical name."
// Originally "Your horizon" (James, 3 Sep: "the field view of the
// schedule and the upcoming sites, breaks in the plan just gives them a view
// on the horizon which they appreciate, make it sexy … add in a calendar view
// and then click the site navigates to that project")
//
// A forward-looking personal schedule for anyone with a login — field crew,
// supervisors, directors. Shows the next ~6 weeks as either:
//
//   Timeline — a vertical run of site cards (live site highlighted, days
//     left, plan-ticked progress, night-shift outage chips, plant, key
//     dates) with home-break tiles between swings, plus your own ad-hoc
//     tasks (personal_bookings) threaded in.
//   Calendar — month grids with stint days painted in the project's colour,
//     night shifts marked, today ringed. Tap a coloured day → that project.
//
// Data: site_roster stints ∪ project_team (same two legs as useMyProjectIds /
// app.is_on_project), personal_bookings for ad-hoc, program activities +
// sub-activity ticks for progress/key dates/plant, outage project_bookings
// for night shifts. Read-only — every tap navigates to /projects/:id where
// the build plan, program and bookings live.
//
// TIMEZONE: local day keys ONLY (localKey). Never toISOString().slice(0,10) —
// that bit us on the schedule (task on the 14th shown on the 13th).
// =============================================================================

import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CalendarRange, ChevronDown, ClipboardCheck, ExternalLink,
  HardHat, Home, LayoutGrid, List as ListIcon, MapPin, Moon, Phone, Star,
  Truck, Wrench,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Spinner, EmptyState } from "@/components/ui";
import { format } from "date-fns";

// ---- local-day helpers (the only date arithmetic allowed on day fields) ----
const pad2 = (n: number) => String(n).padStart(2, "0");
const localKey = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const keyToDate = (k: string) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDaysKey = (k: string, n: number) => {
  const d = keyToDate(k);
  d.setDate(d.getDate() + n);
  return localKey(d);
};
/** days from a → b (b later ⇒ positive) */
const diffDays = (a: string, b: string) =>
  Math.round((keyToDate(b).getTime() - keyToDate(a).getTime()) / 86_400_000);

// Same stable per-project palette as MyWeekScreen — "the orange one is
// Coleambally" holds across both surfaces.
const PROJECT_COLOURS = [
  "#FF7844", "#60A5FA", "#34D399", "#A78BFA",
  "#FBBF24", "#EC4899", "#22D3EE", "#F87171",
];
function projectColour(projectId: string): string {
  let h = 0;
  for (let i = 0; i < projectId.length; i++) h = (h * 31 + projectId.charCodeAt(i)) >>> 0;
  return PROJECT_COLOURS[h % PROJECT_COLOURS.length];
}

// "Sharon_R008" → "SHRN": keep the first letter, drop vowels/spaces after it.
function shortCode(name: string): string {
  const letters = (name[0] + name.slice(1).replace(/[aeiou]/gi, "")).replace(/[^A-Za-z0-9]/g, "");
  return letters.slice(0, 4).toUpperCase();
}

const PROJECT_NOT_RUNNING = new Set([
  "closed", "cancelled", "lost", "acceptance_pending", "on_hold",
]);

const HORIZON_DAYS = 42;   // stat window + calendar span
const FETCH_DAYS   = 70;   // fetch a bit further so "firming up" stints show

interface Stint {
  project_id: string;
  project_code: string;
  project_name: string;
  site_name: string | null;
  site_suburb: string | null;
  site_state: string | null;
  from: string;            // local day key
  to: string | null;       // null = open-ended
  // Mig 400 (James, 29 Sep: "Mount Tamborine shows 18 site days for a local
  // Mon–Fri job"): the person's own flag, else the booking's. When true the
  // Saturdays and Sundays inside the stint are home days, not site days.
  weekendsOff: boolean;
}

const isWeekendKey = (k: string) => { const d = keyToDate(k).getDay(); return d === 0 || d === 6; };
// Is this person on site on day k under this stint?
const onSite = (st: Stint, k: string): boolean =>
  st.from <= k && (st.to === null || st.to >= k) && !(st.weekendsOff && isWeekendKey(k));
// Site days in [a, z] inclusive for a stint's weekend rule.
function siteDaysBetween(a: string, z: string, weekendsOff: boolean): number {
  if (z < a) return 0;
  const n = diffDays(a, z) + 1;
  if (!weekendsOff) return n;
  let c = 0;
  for (let i = 0; i < n; i++) if (!isWeekendKey(addDaysKey(a, i))) c++;
  return c;
}

interface AdhocTask {
  booking_id: string;
  project_id: string | null;
  title: string;
  scheduled_start: string;
  scheduled_end: string | null;
  location_address: string | null;
  status: string;
}

// -----------------------------------------------------------------------------
// Shared stint fetch — the screen and the portal snapshot use the SAME query
// key + fn, so react-query serves one from the other's cache.
async function fetchMyStints(employeeId: string, todayK: string, fetchEndK: string): Promise<Stint[]> {
  const projSel = `
    project_code, project_name, status,
    scheduled_start_date, scheduled_end_date,
    actual_start_date, actual_end_date,
    site:site_id ( site_name, suburb, state )
  `;
  const [rosterQ, teamQ] = await Promise.all([
    (supabase as any)
      .from("site_roster")
      .select(`project_id, on_site_from, on_site_to, weekends_off, booking:pca_id ( assigned_to, weekends_off ), project:project_id (${projSel})`)
      .eq("employee_id", employeeId)
      .or(`on_site_to.is.null,on_site_to.gte.${todayK}`),
    (supabase as any)
      .from("project_team")
      .select(`project_id, assigned_from, assigned_to, project:project_id (${projSel})`)
      .eq("employee_id", employeeId)
      .or(`assigned_to.is.null,assigned_to.gte.${todayK}`),
  ]);
  if (rosterQ.error) throw rosterQ.error;
  if (teamQ.error) throw teamQ.error;

  const out: Stint[] = [];
  const rosterProjects = new Set<string>();

  for (const r of (rosterQ.data ?? []) as any[]) {
    const p = r.project;
    if (!p || !r.project_id || !r.on_site_from) continue;
    if (PROJECT_NOT_RUNNING.has(p.status)) continue;
    const to = r.on_site_to ?? r.booking?.assigned_to ?? null;
    if (to && to < todayK) continue;
    if (r.on_site_from > fetchEndK) continue;
    rosterProjects.add(r.project_id);
    out.push({
      project_id: r.project_id,
      project_code: p.project_code ?? "",
      project_name: p.project_name ?? "",
      site_name: p.site?.site_name ?? null,
      site_suburb: p.site?.suburb ?? null,
      site_state: p.site?.state ?? null,
      from: r.on_site_from,
      to,
      weekendsOff: r.weekends_off ?? r.booking?.weekends_off ?? false,
    });
  }

  // team fallback — only for projects with NO roster stint, using the
  // project's effective run dates (same gating as MyWeekScreen.covers)
  for (const t of (teamQ.data ?? []) as any[]) {
    const p = t.project;
    if (!p || !t.project_id || rosterProjects.has(t.project_id)) continue;
    if (PROJECT_NOT_RUNNING.has(p.status)) continue;
    const projStart = p.actual_start_date ?? p.scheduled_start_date ?? null;
    const from =
      projStart && t.assigned_from
        ? (projStart > t.assigned_from ? projStart : t.assigned_from)
        : (projStart ?? t.assigned_from ?? null);
    const to = p.actual_end_date ?? p.scheduled_end_date ?? t.assigned_to ?? null;
    if (!from) continue;
    if (to && to < todayK) continue;
    if (from > fetchEndK) continue;
    out.push({
      project_id: t.project_id,
      project_code: p.project_code ?? "",
      project_name: p.project_name ?? "",
      site_name: p.site?.site_name ?? null,
      site_suburb: p.site?.suburb ?? null,
      site_state: p.site?.state ?? null,
      from, to,
      weekendsOff: false,
    });
  }

  // merge same-project stints that touch/overlap (gap ≤ 1 day, same weekend rule)
  out.sort((a, b) => a.from.localeCompare(b.from));
  const merged: Stint[] = [];
  for (const st of out) {
    const prev = merged.find(m =>
      m.project_id === st.project_id &&
      m.weekendsOff === st.weekendsOff &&
      (m.to === null || st.from <= addDaysKey(m.to, 1)) &&
      st.from >= m.from);
    if (prev) {
      if (prev.to !== null && (st.to === null || st.to > prev.to)) prev.to = st.to;
    } else {
      merged.push({ ...st });
    }
  }
  merged.sort((a, b) => a.from.localeCompare(b.from) || a.project_name.localeCompare(b.project_name));
  return merged;
}

// -----------------------------------------------------------------------------
export default function MyHorizonScreen() {
  const user = useRequireAuth();
  const employeeId = (user as any)?.employee_id ?? null;
  const [view, setView] = useState<"timeline" | "calendar">("timeline");

  const todayK = localKey(new Date());
  const fetchEndK = addDaysKey(todayK, FETCH_DAYS);
  const horizonEndK = addDaysKey(todayK, HORIZON_DAYS - 1);

  // ---- leg 1+2: my stints (roster ∪ team, mirroring useMyProjectIds) ----
  const { data: stints = [], isLoading: stintsLoading } = useQuery({
    queryKey: ["my_horizon_stints", employeeId],
    enabled: !!employeeId,
    queryFn: () => fetchMyStints(employeeId, todayK, fetchEndK),
  });

  const projectIds = useMemo(
    () => Array.from(new Set(stints.map(s => s.project_id))),
    [stints],
  );

  // ---- ad-hoc tasks + per-project extras (program, ticks, outages) ----
  const { data: extras, isLoading: extrasLoading } = useQuery({
    queryKey: ["my_horizon_extras", employeeId, projectIds.join(",")],
    enabled: !!employeeId && !stintsLoading,
    queryFn: async () => {
      const [mineQ, actsQ, subsQ, outagesQ] = await Promise.all([
        (supabase as any)
          .from("personal_bookings")
          .select("booking_id, project_id, title, scheduled_start, scheduled_end, location_address, status")
          .eq("assignee_employee_id", employeeId)
          .in("status", ["scheduled", "in_progress"])
          .order("scheduled_start"),
        projectIds.length
          ? (supabase as any)
              .from("project_program_activities")
              .select("project_id, activity_id, name, kind, start_date, end_date, is_key, plant_needed, sort_order, in_build_plan")
              .in("project_id", projectIds)
              .eq("is_active", true)
              .not("start_date", "is", null)
              .order("sort_order")
          : Promise.resolve({ data: [], error: null }),
        projectIds.length
          ? (supabase as any)
              .from("project_program_subactivities")
              // Scoped to live build-plan lines only — the "x/y plan ticked"
              // chip counts the plan, so subs left on archived revisions or on
              // lines that never went to the field must not inflate it.
              .select("sub_id, activity_id, project_id, name, sort_order, done, activity:activity_id!inner(is_active, in_build_plan)")
              .eq("activity.is_active", true)
              .eq("activity.in_build_plan", true)
              .in("project_id", projectIds)
          : Promise.resolve({ data: [], error: null }),
        projectIds.length
          ? (supabase as any)
              .from("project_bookings")
              .select("booking_id, project_id, title, booking_type, status, scheduled_start")
              .in("project_id", projectIds)
              .eq("booking_type", "outage")
              .neq("status", "cancelled")
              .gte("scheduled_start", new Date().toISOString())
              .order("scheduled_start")
          : Promise.resolve({ data: [], error: null }),
      ]);
      for (const q of [mineQ, actsQ, subsQ, outagesQ]) if (q.error) throw q.error;
      return {
        mine:    (mineQ.data ?? []) as AdhocTask[],
        acts:    (actsQ.data ?? []) as any[],
        subs:    (subsQ.data ?? []) as any[],
        outages: (outagesQ.data ?? []) as any[],
      };
    },
  });

  if (!user) return null;
  const loading = stintsLoading || extrasLoading;

  // ---- derived, all cheap ----
  const acts = extras?.acts ?? [];
  const subs = extras?.subs ?? [];
  const outages = extras?.outages ?? [];
  const adhoc = (extras?.mine ?? []).filter(t => {
    if (t.status === "in_progress") return true; // still open = still yours
    const endK = localKey(new Date(t.scheduled_end ?? t.scheduled_start));
    const startK = localKey(new Date(t.scheduled_start));
    return endK >= todayK && startK <= fetchEndK;
  });

  const subAgg = new Map<string, { done: number; total: number }>();
  for (const su of subs) {
    const a = subAgg.get(su.project_id) ?? { done: 0, total: 0 };
    a.total += 1;
    if (su.done) a.done += 1;
    subAgg.set(su.project_id, a);
  }
  const actsByProject = new Map<string, any[]>();
  for (const a of acts) {
    const arr = actsByProject.get(a.project_id) ?? [];
    arr.push(a);
    actsByProject.set(a.project_id, arr);
  }
  const subsByActivity = new Map<string, any[]>();
  for (const su of subs) {
    if (!su.activity_id) continue;
    const arr = subsByActivity.get(su.activity_id) ?? [];
    arr.push(su);
    subsByActivity.set(su.activity_id, arr);
  }
  const plantByProject = new Map<string, string[]>();
  for (const a of acts) {
    const plant: string[] = a.plant_needed ?? [];
    if (!plant.length) continue;
    if ((a.end_date ?? a.start_date) < todayK) continue;
    const set = plantByProject.get(a.project_id) ?? [];
    for (const p of plant) if (!set.includes(p)) set.push(p);
    plantByProject.set(a.project_id, set);
  }
  const nextKeyByProject = new Map<string, { name: string; start: string }>();
  for (const a of acts) {
    if (!a.is_key) continue;
    if ((a.end_date ?? a.start_date) < todayK) continue;
    const cur = nextKeyByProject.get(a.project_id);
    if (!cur || a.start_date < cur.start) nextKeyByProject.set(a.project_id, { name: a.name, start: a.start_date });
  }
  const day1Name = (s: Stint): string | null =>
    acts.find(a => a.project_id === s.project_id && a.start_date === s.from)?.name ?? null;
  const nextOutage = (pid: string): string | null =>
    outages.find(o => o.project_id === pid)?.scheduled_start ?? null;
  const outageDays = new Map<string, Set<string>>(); // project → day keys
  for (const o of outages) {
    const set = outageDays.get(o.project_id) ?? new Set<string>();
    set.add(localKey(new Date(o.scheduled_start)));
    outageDays.set(o.project_id, set);
  }

  // day coverage across the horizon window — powers stats + calendar
  const coverage = new Map<string, Stint>();
  for (let i = 0; i < HORIZON_DAYS; i++) {
    const k = addDaysKey(todayK, i);
    const s = stints.find(st => onSite(st, k));   // mig 400 - a Mon–Fri stint's weekend is a home day
    if (s) coverage.set(k, s);
  }
  const adhocDays = new Set<string>();
  for (const t of adhoc) adhocDays.add(localKey(new Date(t.scheduled_start)));

  const siteDays = coverage.size;
  const distinctSites = new Set(Array.from(coverage.values()).map(s => s.project_id)).size;
  const homeDays = HORIZON_DAYS - siteDays;
  const nightShifts = outages.filter(o => {
    const k = localKey(new Date(o.scheduled_start));
    return k >= todayK && k <= horizonEndK;
  }).length;

  const isEmpty = !loading && stints.length === 0 && adhoc.length === 0;

  return (
    <div className="p-4 pb-20 max-w-[640px] mx-auto">
      <Link to="/" className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14} /> Home
      </Link>
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
              Next {Math.round(HORIZON_DAYS / 7)} weeks
            </div>
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">My schedule</h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Where you're headed — tap a site to dig in.
          </div>
        </div>

      </div>

      {/* view toggle */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit">
        <button type="button" onClick={() => setView("timeline")}
          className={`px-3 py-1.5 rounded text-[12px] inline-flex items-center gap-1.5 transition-colors ${
            view === "timeline" ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text"}`}>
          <ListIcon size={12} /> Horizon
        </button>
        <button type="button" onClick={() => setView("calendar")}
          className={`px-3 py-1.5 rounded text-[12px] inline-flex items-center gap-1.5 transition-colors ${
            view === "calendar" ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text"}`}>
          <LayoutGrid size={12} /> Calendar
        </button>
      </div>

      {/* stat strip */}
      {!loading && !isEmpty && (
        <div className="grid grid-cols-4 gap-2 mb-5">
          <StatTile v={siteDays} label="site days" tone="accent" icon={HardHat} />
          <StatTile v={distinctSites} label={distinctSites === 1 ? "site" : "sites"} icon={MapPin} />
          <StatTile v={homeDays} label="days home" tone="good" icon={Home} />
          <StatTile v={nightShifts} label={nightShifts === 1 ? "night shift" : "night shifts"} tone="night" icon={Moon} />
        </div>
      )}

      {loading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : isEmpty ? (
        <EmptyState
          icon={CalendarRange}
          title="Nothing on the horizon yet"
          description="When you're rostered onto a site or given a task, it shows up here."
        />
      ) : view === "calendar" ? (
        <CalendarView
          todayK={todayK}
          horizonEndK={horizonEndK}
          coverage={coverage}
          outageDays={outageDays}
          adhocDays={adhocDays}
        />
      ) : (
        <TimelineView
          todayK={todayK}
          horizonEndK={horizonEndK}
          stints={stints}
          adhoc={adhoc}
          subAgg={subAgg}
          actsByProject={actsByProject}
          subsByActivity={subsByActivity}
          plantByProject={plantByProject}
          nextKeyByProject={nextKeyByProject}
          day1Name={day1Name}
          nextOutage={nextOutage}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function StatTile({ v, label, tone, icon: Icon }: {
  v: number; label: string; tone?: "accent" | "good" | "night"; icon: any;
}) {
  const numC =
    tone === "accent" ? "text-accent" :
    tone === "good"   ? "text-emerald-500" :
    tone === "night"  ? "text-indigo-400" : "text-text";
  const wash =
    tone === "accent" ? "from-accent/[.07]" :
    tone === "good"   ? "from-emerald-500/[.06]" :
    tone === "night"  ? "from-indigo-500/[.06]" : "from-transparent";
  return (
    <Card>
      <div className={`px-2 py-2.5 text-center bg-gradient-to-br ${wash} to-transparent`}>
        <Icon size={13} className={`${numC} opacity-70 mx-auto mb-0.5`} />
        <div className={`text-[19px] font-bold tabular-nums leading-none ${numC}`}>{v}</div>
        <div className="text-[9px] uppercase tracking-wider2 text-text-muted font-mono mt-1">{label}</div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Timeline — vertical run: month labels, stint cards, home breaks, ad-hoc rows
// -----------------------------------------------------------------------------
function TimelineView({
  todayK, horizonEndK, stints, adhoc, subAgg, actsByProject, subsByActivity,
  plantByProject, nextKeyByProject, day1Name, nextOutage,
}: {
  todayK: string;
  horizonEndK: string;
  stints: Stint[];
  adhoc: AdhocTask[];
  subAgg: Map<string, { done: number; total: number }>;
  actsByProject: Map<string, any[]>;
  subsByActivity: Map<string, any[]>;
  plantByProject: Map<string, string[]>;
  nextKeyByProject: Map<string, { name: string; start: string }>;
  day1Name: (s: Stint) => string | null;
  nextOutage: (pid: string) => string | null;
}) {
  // flat, date-sorted items; month headers threaded in as we render
  type Item =
    | { date: string; kind: "stint"; stint: Stint; live: boolean }
    | { date: string; kind: "break"; from: string; to: string; days: number }
    | { date: string; kind: "task"; task: AdhocTask };

  const items: Item[] = [];
  let cursor = todayK;
  for (const s of stints) {
    const live = s.from <= todayK && (s.to === null || s.to >= todayK);
    if (!live && s.from > cursor) {
      const gap = diffDays(cursor, s.from);
      if (gap >= 2) {
        items.push({ date: cursor, kind: "break", from: cursor, to: addDaysKey(s.from, -1), days: gap });
      }
    }
    items.push({ date: live ? todayK : s.from, kind: "stint", stint: s, live });
    const after = s.to ? addDaysKey(s.to, 1) : cursor;
    if (after > cursor) cursor = after;
  }
  for (const t of adhoc) {
    items.push({ date: localKey(new Date(t.scheduled_start)), kind: "task", task: t });
  }
  items.sort((a, b) => a.date.localeCompare(b.date));

  let lastMonth = "";
  return (
    <div className="relative pl-6">
      {/* spine */}
      <div className="absolute left-[9px] top-2 bottom-2 w-[2px] bg-line rounded-full" />
      {items.map((it, i) => {
        const mo = format(keyToDate(it.date), "MMMM");
        const showMonth = mo !== lastMonth;
        lastMonth = mo;
        return (
          <div key={i}>
            {showMonth && (
              <div className="text-[10px] uppercase tracking-wider2 text-text-dim font-mono mb-2 mt-1">{mo}</div>
            )}
            {it.kind === "break" ? (
              <BreakTile from={it.from} to={it.to} days={it.days} />
            ) : it.kind === "task" ? (
              <AdhocRow task={it.task} />
            ) : (
              <StintCard
                stint={it.stint}
                live={it.live}
                todayK={todayK}
                horizonEndK={horizonEndK}
                agg={subAgg.get(it.stint.project_id)}
                acts={actsByProject.get(it.stint.project_id) ?? []}
                subsByActivity={subsByActivity}
                plant={plantByProject.get(it.stint.project_id) ?? []}
                nextKey={nextKeyByProject.get(it.stint.project_id) ?? null}
                day1={day1Name(it.stint)}
                outageTs={it.live ? nextOutage(it.stint.project_id) : null}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Node({ colour }: { colour: string }) {
  return (
    <span
      className="absolute top-5 w-[14px] h-[14px] rounded-full border-[3px] border-ink"
      style={{ backgroundColor: colour, left: "-21px" }}
    />
  );
}

function BreakTile({ from, to, days }: { from: string; to: string; days: number }) {
  const f = keyToDate(from);
  const t = keyToDate(to);
  const label = days <= 2
    ? `Home — ${format(f, "EEE d")}${days === 2 ? ` & ${format(t, "EEE d")}` : ""}`
    : `Home — ${format(f, "EEE d")} → ${format(t, "EEE d MMM")}`;
  return (
    <div className="flex items-center gap-2.5 mb-3 px-3.5 py-2.5 border border-dashed border-line rounded-xl bg-emerald-500/[.04]">
      <Home size={15} className="text-emerald-400 shrink-0" />
      <div>
        <div className="text-[12.5px] font-semibold text-emerald-400">{label}</div>
        <div className="text-[10.5px] text-text-muted">{days} day{days === 1 ? "" : "s"} between swings</div>
      </div>
    </div>
  );
}

function AdhocRow({ task }: { task: AdhocTask }) {
  const start = new Date(task.scheduled_start);
  const inner = (
    <div className="relative flex items-center gap-2.5 mb-3 px-3.5 py-2.5 border border-violet-500/30 rounded-xl bg-violet-500/[.06]">
      <Node colour="#A78BFA" />
      <Wrench size={14} className="text-violet-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-violet-200 truncate">{task.title}</div>
        <div className="text-[10.5px] text-text-muted">
          {format(start, "EEE d MMM · HH:mm")}
          {task.location_address ? ` · ${task.location_address}` : ""} · your task
        </div>
      </div>
    </div>
  );
  return task.project_id
    ? <Link to={`/projects/${task.project_id}`} className="block">{inner}</Link>
    : inner;
}

function StintCard({
  stint, live, todayK, horizonEndK, agg, acts, subsByActivity, plant, nextKey, day1, outageTs,
}: {
  stint: Stint;
  live: boolean;
  todayK: string;
  horizonEndK: string;
  agg?: { done: number; total: number };
  acts: any[];
  subsByActivity: Map<string, any[]>;
  plant: string[];
  nextKey: { name: string; start: string } | null;
  day1: string | null;
  outageTs: string | null;
}) {
  const colour = projectColour(stint.project_id);
  const firmingUp = stint.from > horizonEndK;
  const title = stint.site_name ?? stint.project_name;
  const from = keyToDate(stint.from);
  // Mig 400 - site days, not calendar days: a Mon–Fri stint skips its weekends.
  const daysLeft = live && stint.to ? siteDaysBetween(addDaysKey(todayK, 1), stint.to, stint.weekendsOff) : null;
  const lengthDays = stint.to ? siteDaysBetween(stint.from, stint.to, stint.weekendsOff) : null;
  const where = [stint.site_suburb, stint.site_state].filter(Boolean).join(" ");

  // James, 3 Sep round 3: the three buttons must DO three things. Build plan
  // and Site open panels right here on the card; Program jumps to the project
  // with the full gantt already expanded.
  const [panel, setPanel] = useState<null | "plan" | "site">(null);
  const toggle = (which: "plan" | "site") => setPanel(p => (p === which ? null : which));

  const rangeLabel = live
    ? (stint.to ? `until ${format(keyToDate(stint.to), "EEE d MMM")}` : "on site now")
    : stint.to
      ? `${format(from, "EEE d")} – ${format(keyToDate(stint.to), "EEE d MMM")}`
      : `from ${format(from, "EEE d MMM")}`;

  const hasPlan = acts.length > 0;

  return (
    <div className={`relative mb-3 ${firmingUp ? "opacity-70" : ""}`}>
      <Node colour={firmingUp ? "#8a95a3" : colour} />
      <Card className={live ? "border-accent/40 shadow-[0_2px_10px_rgba(255,120,68,.08)]" : ""}>
        <Link to={`/projects/${stint.project_id}`} className="block">
          <div className={`p-3.5 ${live ? "bg-gradient-to-br from-accent/[.06] to-transparent" : ""}`}>
            <div className="flex items-start gap-3">
              {/* date tile */}
              <div className={`shrink-0 w-[46px] rounded-lg text-center py-1.5 leading-tight border ${
                live ? "bg-accent border-accent" : "bg-ink-raised border-line"}`}>
                <div className={`text-[8.5px] uppercase font-bold ${live ? "text-white/85" : "text-text-muted"}`}>
                  {live ? "now" : format(from, "EEE")}
                </div>
                <div className={`text-[17px] font-bold ${live ? "text-white" : "text-text"}`}>
                  {live ? format(keyToDate(todayK), "d") : format(from, "d")}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-text leading-snug truncate">{title}</div>
                <div className="text-[11px] text-text-muted mt-0.5 truncate">
                  {stint.project_code} · {rangeLabel}
                  {where ? ` · ${where}` : ""}
                  {stint.weekendsOff ? " · Mon–Fri" : ""}
                  {firmingUp ? " · dates firming up" : ""}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {live && daysLeft !== null && (
                    <Chip tone="plain">{daysLeft === 0 ? "last day" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}</Chip>
                  )}
                  {!live && lengthDays !== null && (
                    <Chip tone="plain">{firmingUp ? `~${lengthDays} days` : `${lengthDays} day${lengthDays === 1 ? "" : "s"}`}</Chip>
                  )}
                  {agg && agg.total > 0 && (
                    <Chip tone="accent">{agg.done}/{agg.total} plan ticked</Chip>
                  )}
                  {outageTs && (
                    <Chip tone="night">
                      <Moon size={9} className="inline -mt-px mr-0.5" />
                      outage {format(new Date(outageTs), "EEE HH:mm")}
                    </Chip>
                  )}
                  {!live && plant.length > 0 && (
                    <Chip tone="plant">
                      <Truck size={9} className="inline -mt-px mr-0.5" />
                      {plant.slice(0, 3).join(" · ")}
                    </Chip>
                  )}
                  {nextKey && nextKey.start >= stint.from && (!stint.to || nextKey.start <= stint.to) && (
                    <Chip tone="key">
                      <Star size={9} className="inline -mt-px mr-0.5" />
                      {nextKey.name} · {format(keyToDate(nextKey.start), "EEE d")}
                    </Chip>
                  )}
                </div>
                {!live && day1 && (
                  <div className="text-[11px] text-text-muted mt-1.5 truncate">Day 1: {day1}</div>
                )}
              </div>
            </div>
          </div>
        </Link>
        {/* nav row — Build plan and Site open IN-CARD panels; Program jumps
            to the project with the full gantt open */}
        {!firmingUp && (
          <div className="flex border-t border-line bg-ink-raised/30">
            <NavToggle
              icon={ClipboardCheck}
              label="Build plan"
              active={panel === "plan"}
              onClick={() => toggle("plan")}
            />
            <NavLinkBtn to={`/projects/${stint.project_id}#program`} icon={CalendarRange} label="Program" />
            <NavToggle
              icon={MapPin}
              label="Site"
              active={panel === "site"}
              onClick={() => toggle("site")}
              last
            />
          </div>
        )}
        {panel === "plan" && (
          <PlanPanel
            stint={stint}
            acts={acts}
            subsByActivity={subsByActivity}
            todayK={todayK}
            hasPlan={hasPlan}
          />
        )}
        {panel === "site" && <SitePanel stint={stint} />}
      </Card>
    </div>
  );
}

function NavToggle({ icon: Icon, label, active, onClick, last }: {
  icon: any; label: string; active: boolean; onClick: () => void; last?: boolean;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`flex-1 text-center py-2 text-[11px] font-semibold transition-colors ${
        active ? "bg-accent/10 text-accent" : "text-accent hover:bg-accent/10"} ${
        last ? "" : "border-r border-line"}`}>
      <Icon size={13} className="inline -mt-px mr-1" />
      {label}
      <ChevronDown size={11} className={`inline -mt-px ml-1 transition-transform ${active ? "rotate-180" : ""}`} />
    </button>
  );
}

function NavLinkBtn({ to, icon: Icon, label }: { to: string; icon: any; label: string }) {
  return (
    <Link to={to}
      className="flex-1 text-center py-2 text-[11px] font-semibold text-accent hover:bg-accent/10 transition-colors border-r border-line">
      <Icon size={13} className="inline -mt-px mr-1" />
      {label}
    </Link>
  );
}

// -----------------------------------------------------------------------------
// PlanPanel — the build plan, in place: program lines in date order with
// their sub-activities, tickable right here (same one write as the field
// card; RLS lets managers and anyone on the project tick).
// -----------------------------------------------------------------------------
function PlanPanel({ stint, acts, subsByActivity, todayK, hasPlan }: {
  stint: Stint;
  acts: any[];
  subsByActivity: Map<string, any[]>;
  todayK: string;
  hasPlan: boolean;
}) {
  const qc = useQueryClient();
  const [showEarlier, setShowEarlier] = useState(false);

  async function tick(su: any) {
    const { error } = await (supabase as any)
      .from("project_program_subactivities")
      .update({ done: !su.done })
      .eq("sub_id", su.sub_id);
    if (!error) qc.invalidateQueries({ queryKey: ["my_horizon_extras"] });
  }

  if (!hasPlan) {
    return (
      <div className="border-t border-line px-4 py-3 text-[11.5px] text-text-muted">
        No build program loaded for this job yet — when one's imported, the plan shows here.
      </div>
    );
  }

  const LANE_C: Record<string, string> = {
    CIVILS: "#4f97d7", STRUCTURE: "#7a8aa0", ICIO: "#e2661a", POWER: "#37a97f", ACCEPT: "#9b7bd4",
  };
  // Coretel's work only (mig 348) — the guys don't need the subbie and client
  // lines here; those live one tap away under Program.
  const mine = [...acts]
    .filter(a => a.start_date && a.in_build_plan)
    .sort((a, b) => (a.start_date as string).localeCompare(b.start_date) || (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const otherCount = acts.filter(a => !a.in_build_plan).length;

  if (mine.length === 0) {
    return (
      <div className="border-t border-line px-4 py-3 text-[11.5px] text-text-muted">
        No lines are flagged for the field build plan yet — a manager can tick
        "Field build plan" on lines in the program editor.
        {otherCount > 0 && <> The full program ({otherCount} lines) is under Program.</>}
      </div>
    );
  }

  const earlier = mine.filter(a => (a.end_date ?? a.start_date) < todayK);
  const current = mine.filter(a => (a.end_date ?? a.start_date) >= todayK);

  const fmtD = (k: string) => format(keyToDate(k), "d MMM");

  return (
    <div className="border-t border-line max-h-[380px] overflow-y-auto">
      {earlier.length > 0 && (
        <button type="button" onClick={() => setShowEarlier(v => !v)}
          className="w-full text-left px-3.5 py-2 text-[10.5px] font-mono text-text-muted hover:text-text border-b border-line/60">
          <ChevronDown size={10} className={`inline -mt-px mr-1 transition-transform ${showEarlier ? "rotate-180" : ""}`} />
          {earlier.length} earlier line{earlier.length === 1 ? "" : "s"} finished before today
        </button>
      )}
      {(showEarlier ? mine : current).map((a: any) => {
        const subs = subsByActivity.get(a.activity_id) ?? [];
        const onNow = a.start_date <= todayK && (a.end_date ?? a.start_date) >= todayK;
        const past = (a.end_date ?? a.start_date) < todayK;
        return (
          <div key={a.activity_id}
               className={`px-3.5 py-2 border-b border-line/60 last:border-b-0 ${
                 onNow ? "bg-accent/[.05]" : past ? "opacity-55" : ""}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-1 h-7 rounded-sm shrink-0"
                    style={{ backgroundColor: LANE_C[a.lane] ?? "#8a95a3" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium text-text truncate">
                  {a.kind === "milestone" && <Star size={10} className="inline -mt-px mr-1 text-accent" />}
                  {a.kind === "outage" && <Moon size={10} className="inline -mt-px mr-1 text-indigo-400" />}
                  {a.name}
                  {onNow && <span className="text-[9px] font-bold text-accent ml-1.5 uppercase">now</span>}
                </div>
                <div className="text-[10.5px] text-text-muted font-mono">
                  {fmtD(a.start_date)}{a.end_date && a.end_date !== a.start_date ? ` – ${fmtD(a.end_date)}` : ""}
                  {(a.plant_needed ?? []).length > 0 && (
                    <span className="text-amber-500"> · {(a.plant_needed as string[]).join(" · ")}</span>
                  )}
                </div>
              </div>
            </div>
            {subs.length > 0 && (
              <div className="mt-1.5 ml-3.5 space-y-1">
                {[...subs].sort((x, y) => (x.sort_order ?? 0) - (y.sort_order ?? 0)).map((su: any) => (
                  <button key={su.sub_id} type="button" onClick={() => void tick(su)}
                    className="flex items-center gap-2 w-full text-left group">
                    <span className={`shrink-0 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all group-active:scale-90 ${
                      su.done ? "bg-emerald-500 border-emerald-500" : "border-line group-hover:border-accent"}`}>
                      {su.done && <span className="text-white text-[10px] leading-none">✓</span>}
                    </span>
                    <span className={`text-[11.5px] ${su.done ? "text-text-muted line-through" : "text-text"}`}>
                      {su.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="px-3.5 py-2 bg-ink-raised/30 flex items-center justify-between gap-3">
        <Link to={`/projects/${stint.project_id}#plan`} className="text-[10.5px] font-mono text-accent hover:underline">
          Open today's work on the project →
        </Link>
        {otherCount > 0 && (
          <Link to={`/projects/${stint.project_id}#program`} className="text-[10.5px] font-mono text-text-muted hover:text-accent">
            +{otherCount} subbie/client lines → Program
          </Link>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SitePanel — site details in place: address with a directions link, site
// code, PM + supervisor numbers (app.project_contacts, permission-safe),
// and quick nav. Fetched lazily when the drop-down opens.
// -----------------------------------------------------------------------------
function SitePanel({ stint }: { stint: Stint }) {
  const { data, isLoading } = useQuery({
    queryKey: ["horizon_site_panel", stint.project_id],
    queryFn: async () => {
      const [projQ, contactsQ] = await Promise.all([
        supabase
          .from("projects")
          .select("project_id, sites(site_code, site_name, address_line1, suburb, state, postcode, latitude, longitude)")
          .eq("project_id", stint.project_id)
          .maybeSingle(),
        supabase.schema("app").rpc("project_contacts" as any, { p_project_id: stint.project_id }),
      ]);
      if (projQ.error) throw projQ.error;
      // contacts can fail closed for odd roles — treat as empty, not fatal
      return {
        site: (projQ.data as any)?.sites ?? null,
        contacts: (contactsQ.error ? [] : (contactsQ.data ?? [])) as
          { contact_role: string; full_name: string; mobile: string | null }[],
      };
    },
  });

  if (isLoading) {
    return <div className="border-t border-line px-4 py-4 flex justify-center"><Spinner size={14} /></div>;
  }
  const site = data?.site;
  const addr = site
    ? [site.address_line1, site.suburb, site.state, site.postcode].filter(Boolean).join(", ")
    : null;
  // Directions target: GPS coordinates first, then the real street address.
  // NEVER the bare suburb/town — "Beechmont" sent a crew to the middle of a
  // township instead of the compound (James, 9 Sep 2026). If the site row has
  // neither coordinates nor an address_line1, there is no directions link at
  // all; the crew rings the PM rather than trusting a town-level pin.
  const lat = site?.latitude ?? null;
  const lng = site?.longitude ?? null;
  const destination =
    lat != null && lng != null
      ? `${lat},${lng}`
      : site?.address_line1
        ? addr
        : null;
  // dir/?api=1 is Google's documented cross-platform directions link — opens
  // the Maps app on a phone, the web app on a desktop (same as SiteCard on the
  // office project screen).
  const mapsUrl = destination
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    : null;

  return (
    <div className="border-t border-line px-4 py-3 space-y-2.5">
      <div>
        <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-0.5">Site</div>
        <div className="text-[13px] font-medium text-text">
          {site?.site_name ?? stint.site_name ?? stint.project_name}
          {site?.site_code && <span className="text-text-muted font-mono text-[11px] ml-2">{site.site_code}</span>}
        </div>
        {(addr || mapsUrl) && (
          <div className="text-[12px] text-text-muted mt-0.5">
            {addr}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer"
                 className={`text-accent hover:underline whitespace-nowrap ${addr ? "ml-2" : ""}`}
                 title={lat != null && lng != null ? "Directions to the site coordinates" : "Directions to the site address"}>
                <ExternalLink size={10} className="inline -mt-px mr-0.5" />directions
              </a>
            )}
          </div>
        )}
      </div>
      {(data?.contacts ?? []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1">Who to call</div>
          <div className="space-y-1">
            {data!.contacts.map(c => (
              <div key={c.contact_role} className="flex items-center gap-2 text-[12px]">
                <span className="text-text-muted w-[110px] shrink-0">{c.contact_role}</span>
                <span className="text-text font-medium">{c.full_name}</span>
                {c.mobile && (
                  <a href={`tel:${c.mobile}`} className="text-accent hover:underline ml-auto">
                    <Phone size={10} className="inline -mt-px mr-1" />{c.mobile}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="pt-1 flex gap-4">
        <Link to={`/projects/${stint.project_id}`} className="text-[10.5px] font-mono text-accent hover:underline">
          Open project →
        </Link>
        <Link to={`/projects/${stint.project_id}#program`} className="text-[10.5px] font-mono text-accent hover:underline">
          Program →
        </Link>
      </div>
    </div>
  );
}

function Chip({ children, tone }: { children: ReactNode; tone: "plain" | "accent" | "night" | "plant" | "key" }) {
  const cls = {
    plain:  "bg-ink-raised text-text-muted border border-line",
    accent: "bg-accent/10 text-accent border border-accent/25",
    night:  "bg-indigo-900/60 text-indigo-200 border border-indigo-700/50",
    plant:  "bg-amber-500/10 text-amber-400 border border-amber-500/25",
    key:    "bg-accent/10 text-accent border border-accent/25",
  }[tone];
  return (
    <span className={`text-[10px] font-semibold px-2 py-[2px] rounded-full whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Calendar — stacked month grids, stint days painted in project colour.
// Legend up top, weekend tint, per-month site-day chip, weeks past the
// horizon trimmed (no acre of empty October). Tap a coloured day → project.
// -----------------------------------------------------------------------------
function CalendarView({
  todayK, horizonEndK, coverage, outageDays, adhocDays,
}: {
  todayK: string;
  horizonEndK: string;
  coverage: Map<string, Stint>;
  outageDays: Map<string, Set<string>>;
  adhocDays: Set<string>;
}) {
  const navigate = useNavigate();

  // months spanned by [today .. horizon end]
  const months: Date[] = [];
  {
    const start = keyToDate(todayK);
    let m = new Date(start.getFullYear(), start.getMonth(), 1);
    const end = keyToDate(horizonEndK);
    while (m <= end) {
      months.push(new Date(m));
      m = new Date(m.getFullYear(), m.getMonth() + 1, 1);
    }
  }

  // legend — every project in the window, first-seen order
  const legend: Stint[] = [];
  for (let i = 0; i < HORIZON_DAYS; i++) {
    const st = coverage.get(addDaysKey(todayK, i));
    if (st && !legend.some(l => l.project_id === st.project_id)) legend.push(st);
  }

  const isNight = (k: string, pid: string) => outageDays.get(pid)?.has(k) ?? false;

  return (
    <div>
      {/* legend first — the key to the colours before you read them */}
      {(legend.length > 0 || adhocDays.size > 0) && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {legend.map(st => (
            <Link key={st.project_id} to={`/projects/${st.project_id}`}
              className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-text bg-ink-raised border border-line rounded-full px-2.5 py-1 hover:border-accent transition-colors">
              <span className="w-[9px] h-[9px] rounded-[3px]" style={{ backgroundColor: projectColour(st.project_id) }} />
              {st.site_name ?? st.project_name}
            </Link>
          ))}
          {adhocDays.size > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-text-muted bg-ink-raised border border-line rounded-full px-2.5 py-1">
              <span className="w-[9px] h-[9px] rounded-[3px] bg-violet-400/70" />
              your tasks
            </span>
          )}
          {Array.from(outageDays.values()).some(x => x.size > 0) && (
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-text-muted bg-ink-raised border border-line rounded-full px-2.5 py-1">
              <Moon size={10} className="text-indigo-400" /> night shift
            </span>
          )}
        </div>
      )}

      {months.map(mStart => {
        const mKey = format(mStart, "yyyy-MM");
        // Monday on/before the 1st
        const first = new Date(mStart);
        const dow = (first.getDay() + 6) % 7; // Mon=0
        first.setDate(first.getDate() - dow);
        const monthEnd = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 0);
        const cells: Date[] = [];
        for (let d = new Date(first); d <= monthEnd || (d.getDay() + 6) % 7 !== 0; d.setDate(d.getDate() + 1)) {
          cells.push(new Date(d));
          if (cells.length > 42) break;
        }
        // trim whole weeks that sit entirely past the horizon window
        const weeks: Date[][] = [];
        for (let w = 0; w < cells.length; w += 7) weeks.push(cells.slice(w, w + 7));
        const shown = weeks.filter(w => w.some(d => localKey(d) <= horizonEndK));
        if (!shown.length) return null;

        // site days this month (within the window)
        const monthSiteDays = Array.from(coverage.keys())
          .filter(k => k.startsWith(mKey)).length;

        return (
          <Card key={mKey} className="mb-3">
            <div className="p-3.5">
              <div className="flex items-baseline justify-between mb-2.5 px-0.5">
                <div className="text-[14px] font-bold text-text">
                  {format(mStart, "MMMM")}
                  <span className="text-text-muted font-medium text-[12px] ml-1.5">{format(mStart, "yyyy")}</span>
                </div>
                {monthSiteDays > 0 && (
                  <span className="text-[10px] font-semibold text-accent bg-accent/10 border border-accent/20 rounded-full px-2 py-[2px]">
                    {monthSiteDays} site day{monthSiteDays === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
                  <div key={d} className={`text-center text-[8.5px] uppercase tracking-wide font-bold ${
                    d === "Sat" || d === "Sun" ? "text-text-dim/60" : "text-text-dim"}`}>{d[0]}</div>
                ))}
              </div>
              <div className="space-y-1">
                {shown.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {week.map((d, i) => {
                      const k = localKey(d);
                      const inMonth = d.getMonth() === mStart.getMonth();
                      const cover = inMonth ? coverage.get(k) : undefined;
                      const isWE = d.getDay() === 0 || d.getDay() === 6;
                      const isToday = k === todayK;
                      const adhocOnly = inMonth && !cover && adhocDays.has(k);
                      const past = inMonth && k < todayK;

                      let radius = "10px";
                      if (cover) {
                        const prevSame = i !== 0 && coverage.get(addDaysKey(k, -1))?.project_id === cover.project_id;
                        const nextSame = i !== 6 && coverage.get(addDaysKey(k, 1))?.project_id === cover.project_id;
                        if (prevSame && nextSame) radius = "4px";
                        else if (nextSame) radius = "10px 4px 4px 10px";
                        else if (prevSame) radius = "4px 10px 10px 4px";
                      }
                      const runStart = cover && coverage.get(addDaysKey(k, -1))?.project_id !== cover.project_id;

                      return (
                        <button
                          key={k}
                          type="button"
                          disabled={!cover && !adhocOnly}
                          onClick={() => cover && navigate(`/projects/${cover.project_id}`)}
                          className={`relative h-[46px] flex flex-col items-center justify-center text-[12.5px] font-semibold transition-all active:scale-95 ${
                            !inMonth ? "text-transparent"
                            : cover ? "text-white font-bold shadow-sm hover:brightness-105"
                            : adhocOnly ? "text-violet-500 dark:text-violet-300"
                            : past ? "bg-ink-raised/25 text-text-dim/50"
                            : isWE ? "bg-ink-raised/35 text-text-dim"
                            : "bg-ink-raised/70 text-text-muted"} ${
                            isToday ? "ring-2 ring-accent" : ""}`}
                          style={{
                            borderRadius: radius,
                            backgroundColor: cover
                              ? projectColour(cover.project_id)
                              : adhocOnly ? "rgba(139,92,246,.18)" : undefined,
                          }}
                        >
                          {isToday && (
                            <span className="absolute -top-[7px] left-1/2 -translate-x-1/2 text-[6.5px] font-bold uppercase tracking-wide bg-accent text-white rounded-full px-1.5 leading-[11px]">
                              today
                            </span>
                          )}
                          {inMonth ? d.getDate() : ""}
                          {cover && runStart && (
                            <span className="text-[6.5px] uppercase tracking-wide font-bold opacity-90 leading-none mt-[1px]">
                              {shortCode(cover.site_name ?? cover.project_name)}
                            </span>
                          )}
                          {cover && isNight(k, cover.project_id) && (
                            <Moon size={8} className="absolute top-[3px] right-[4px] text-white/90" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        );
      })}

      <div className="flex items-center justify-between gap-3 mt-1">
        <div className="text-[10.5px] text-text-dim">
          Beyond {Math.round(HORIZON_DAYS / 7)} weeks, dates are still firming up.
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-accent bg-accent/[.06] border border-accent/20 rounded-xl px-3 py-2">
        Tap any coloured day to open that site — build plan, program and bookings are one tap away.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MyScheduleSnapshot — the Personal Portal tile (James's requirements doc,
// point 8): "today's work, next activity … a mini schedule". Rendered on
// PortalHome; shares the stint query cache with the full screen.
// -----------------------------------------------------------------------------
export function MyScheduleSnapshot() {
  const user = useRequireAuth();
  const employeeId = (user as any)?.employee_id ?? null;
  const todayK = localKey(new Date());
  const fetchEndK = addDaysKey(todayK, FETCH_DAYS);

  const { data: stints = [] } = useQuery({
    queryKey: ["my_horizon_stints", employeeId],
    enabled: !!employeeId,
    queryFn: () => fetchMyStints(employeeId, todayK, fetchEndK),
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["my_schedule_snapshot_tasks", employeeId],
    enabled: !!employeeId,
    queryFn: async (): Promise<AdhocTask[]> => {
      const { data, error } = await (supabase as any)
        .from("personal_bookings")
        .select("booking_id, project_id, title, scheduled_start, scheduled_end, location_address, status")
        .eq("assignee_employee_id", employeeId)
        .in("status", ["scheduled", "in_progress"])
        .order("scheduled_start")
        .limit(10);
      if (error) throw error;
      return (data ?? []) as AdhocTask[];
    },
  });

  if (!employeeId) return null;

  const todayStint = stints.find(st => onSite(st, todayK));   // mig 400 - Saturday on a Mon–Fri job = home
  const todayTask = tasks.find(t =>
    t.status === "in_progress" || localKey(new Date(t.scheduled_start)) === todayK);
  const nextStint = stints.find(st => st.from > todayK) ?? null;
  const nextTask = tasks.find(t => localKey(new Date(t.scheduled_start)) > todayK) ?? null;

  // 14-day mini strip
  const strip: { k: string; colour: string | null; adhoc: boolean }[] = [];
  for (let i = 0; i < 14; i++) {
    const k = addDaysKey(todayK, i);
    const st = stints.find(x => onSite(x, k));
    const hasTask = tasks.some(t => localKey(new Date(t.scheduled_start)) === k);
    strip.push({ k, colour: st ? projectColour(st.project_id) : null, adhoc: !st && hasTask });
  }

  const daysLeft = todayStint?.to ? siteDaysBetween(addDaysKey(todayK, 1), todayStint.to, todayStint.weekendsOff) : null;

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <CalendarRange size={16} className="text-accent" />
            <h2 className="text-[14px] font-semibold text-text">My schedule</h2>
          </div>
          <Link to="/my-schedule" className="text-[11px] font-mono text-accent hover:underline">
            Open →
          </Link>
        </div>

        <div className="space-y-2">
          {/* today */}
          <Link to={todayStint ? `/projects/${todayStint.project_id}` : "/my-schedule"}
                className="flex items-center gap-2.5 group">
            <span className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: todayStint ? projectColour(todayStint.project_id) : todayTask ? "#A78BFA" : "#34D399" }} />
            <span className="min-w-0 flex-1">
              <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mr-2">Today</span>
              <span className="text-[13px] text-text group-hover:text-accent transition-colors">
                {todayStint
                  ? <>{todayStint.site_name ?? todayStint.project_name}
                      {daysLeft !== null && <span className="text-text-muted"> · {daysLeft === 0 ? "last day" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}</span>}</>
                  : todayTask
                    ? <>{todayTask.title}<span className="text-text-muted"> · your task</span></>
                    : <span className="text-text-muted">Home — nothing on site</span>}
              </span>
            </span>
          </Link>
          {/* next */}
          {(nextStint || nextTask) && (
            <Link to={nextStint ? `/projects/${nextStint.project_id}` : "/my-schedule"}
                  className="flex items-center gap-2.5 group">
              <span className="w-2 h-2 rounded-full flex-shrink-0 opacity-70"
                    style={{ backgroundColor: nextStint ? projectColour(nextStint.project_id) : "#A78BFA" }} />
              <span className="min-w-0 flex-1">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mr-2">Next</span>
                <span className="text-[13px] text-text group-hover:text-accent transition-colors">
                  {nextStint
                    ? <>{nextStint.site_name ?? nextStint.project_name}
                        <span className="text-text-muted"> · from {format(keyToDate(nextStint.from), "EEE d MMM")}</span></>
                    : <>{nextTask!.title}
                        <span className="text-text-muted"> · {format(new Date(nextTask!.scheduled_start), "EEE d MMM")}</span></>}
                </span>
              </span>
            </Link>
          )}
        </div>

        {/* 14-day mini strip */}
        <div className="mt-3 flex gap-[3px]">
          {strip.map((c, i) => (
            <span key={c.k}
              title={format(keyToDate(c.k), "EEE d MMM")}
              className={`h-[10px] flex-1 rounded-[3px] ${i === 0 ? "ring-1 ring-accent" : ""} ${!c.colour && !c.adhoc ? "bg-ink-raised" : ""}`}
              style={{ backgroundColor: c.colour ?? (c.adhoc ? "rgba(139,92,246,.45)" : undefined) }} />
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[9px] text-text-dim font-mono">
          <span>today</span><span>+2 weeks</span>
        </div>
      </div>
    </Card>
  );
}
